import React, { createContext, forwardRef, useContext, useMemo, useState, } from 'react';
import { FlexDirection, Align, PositionType, } from './renderer.js';
import { parseColor } from './utils.js';
export const MaterialContext = createContext({});
export const ThemeContext = createContext({});
const tempColor = new Float32Array(4);
export const Container = forwardRef((props, ref) => {
    return React.createElement('container', Object.assign(Object.assign({}, props), { ref: ref }), props.children);
});
Container.displayName = 'Container';
export const Panel = forwardRef((props, ref) => {
    var _a, _b, _c, _d;
    const context = useContext(MaterialContext);
    const mat = useMemo(() => { var _a; return (_a = context.panelMaterial) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    mat &&
        mat.setColor(parseColor((_a = props.backgroundColor) !== null && _a !== void 0 ? _a : 'fff', tempColor));
    const bmat = useMemo(() => { var _a; return (_a = context.panelMaterial) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    bmat &&
        bmat.setColor(parseColor((_b = props.borderColor) !== null && _b !== void 0 ? _b : 'fff', tempColor));
    return React.createElement('roundedRectangle', Object.assign(Object.assign({}, props), { material: (_c = props.material) !== null && _c !== void 0 ? _c : mat, borderMaterial: (_d = props.borderMaterial) !== null && _d !== void 0 ? _d : bmat, ref: ref }), props.children);
});
Panel.displayName = 'Panel';
export const Panel9Slice = forwardRef((props, ref) => {
    var _a;
    const context = useContext(MaterialContext);
    const mat = useMemo(() => { var _a; return (_a = context.panelMaterialTextured) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    if (mat && props.texture)
        mat.flatTexture = props.texture;
    return React.createElement('nineSlice', Object.assign(Object.assign({}, props), { material: (_a = props.material) !== null && _a !== void 0 ? _a : mat, ref: ref }), props.children);
});
Panel9Slice.displayName = 'Panel9Slice';
export const Image = forwardRef((props, ref) => {
    var _a;
    const context = useContext(MaterialContext);
    const mat = (_a = props.material) !== null && _a !== void 0 ? _a : useMemo(() => { var _a; return (_a = context.panelMaterialTextured) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    const texture = typeof props.src === 'string'
        ? useMemo(() => mat.engine.textures.load(props.src), [props.src])
        : Promise.resolve(props.src);
    texture.then((t) => (mat.flatTexture = t));
    return (React.createElement(Panel, Object.assign({}, props, { material: mat, ref: ref }), props.children));
});
Image.displayName = 'Image';
export const Plane = forwardRef((props, ref) => {
    return React.createElement('mesh', Object.assign(Object.assign({}, props), { ref: ref }), props.children);
});
Plane.displayName = 'Plane';
export const Column = forwardRef((props, ref) => {
    return (React.createElement(Container, Object.assign({ flexDirection: FlexDirection.Column }, props, { ref: ref }), props.children));
});
Column.displayName = 'Column';
export const Row = forwardRef((props, ref) => {
    return (React.createElement(Container, Object.assign({ flexDirection: FlexDirection.Row }, props, { ref: ref }), props.children));
});
Row.displayName = 'Row';
export const Text = forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const context = useContext(MaterialContext);
    const theme = useContext(ThemeContext);
    const mat = (_a = props.material) !== null && _a !== void 0 ? _a : useMemo(() => { var _a; return (_a = context.textMaterial) === null || _a === void 0 ? void 0 : _a.clone(); }, []);
    if (mat) {
        mat.setColor(parseColor((_d = (_b = props.color) !== null && _b !== void 0 ? _b : (_c = theme.colors) === null || _c === void 0 ? void 0 : _c.text) !== null && _d !== void 0 ? _d : ((_e = props.material) !== null && _e !== void 0 ? _e : context.textMaterial)
            .color, tempColor));
    }
    return React.createElement('text3d', Object.assign(Object.assign({}, props), { material: mat, text: (_g = (_f = props.children) === null || _f === void 0 ? void 0 : _f.toString()) !== null && _g !== void 0 ? _g : props.text, ref: ref }));
});
Text.displayName = 'Text';
export const Button = forwardRef((props, ref) => {
    const [hovered, setHovered] = useState(false);
    const [active, setActive] = useState(false);
    let propsMerged = Object.assign(Object.assign(Object.assign({}, props), (hovered ? props.hovered : undefined)), (active ? props.active : undefined));
    return (React.createElement(Panel, Object.assign({}, propsMerged, { onHover: () => setHovered(true), onUnhover: () => setHovered(false), onDown: () => setActive(true), onUp: () => setActive(false), ref: ref }), props.children));
});
Button.displayName = 'Button';
export const ProgressBar = forwardRef((props, ref) => {
    var _a, _b, _c, _d, _e;
    const rounding = (_a = props.rounding) !== null && _a !== void 0 ? _a : 30;
    const value = Math.max(Math.min(1, props.value), 0); // clamp between 0 and 1
    return (React.createElement(Panel, Object.assign({ material: props.bgMaterial, backgroundColor: props.bgColor }, props, { flexDirection: FlexDirection.Row, padding: (_b = props.padding) !== null && _b !== void 0 ? _b : 6, paddingLeft: (_c = props.paddingLeft) !== null && _c !== void 0 ? _c : 8, paddingRight: (_d = props.paddingRight) !== null && _d !== void 0 ? _d : 8, resolution: 6, rounding: rounding * 1.5, ref: ref }),
        React.createElement(Container, { alignItems: Align.FlexStart, position: PositionType.Absolute, width: "100%", height: "100%", left: (_e = props.barLeftMargin) !== null && _e !== void 0 ? _e : 12 }, props.children),
        React.createElement(Panel, { width: `${100 * value}%`, minWidth: rounding * 2, height: "100%", material: props.fgMaterial, backgroundColor: props.fgColor, alignItems: Align.Center, rounding: rounding })));
});
ProgressBar.displayName = 'ProgressBar';
